import { AnalyticsBrowser, Plugin } from '@segment/analytics-next';
import {
  SegmentInterface,
  SessionDataKey,
} from 'services/analytics/segment/types';
import { STORAGE_KEYS } from 'shared/constants/storage';
import {
  AnalyticsConfig,
  EventConfig,
  initializeAnalyticsConfig,
} from 'shared/providers/analytics/AnalyticsConfig';
import { getAppUpdateProperties } from 'shared/utils/appUpdateInfo/appUpdateInfo';
import { displayVersion } from 'shared/utils/appVersionUtils';
import { getItem } from 'shared/utils/asyncStorage';
import { extractCookies } from 'shared/utils/extractCookies/extractCookies.web';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const DEFAULT_TIMEOUT_DURATION = 1800000; // 30 minutes

// Initialize the segment client as a singleton when module loads
const analytics = AnalyticsBrowser.load({
  writeKey: getEnvironmentVar('EXPO_SEGMENT_WEB_API_KEY'),
});

// Session management
let sessionId: number | null = null;
let sessionTimeout: number | null = null;
const marketingSessionData = new Map<SessionDataKey, string>();

/**
 * Generate a new session ID and reset the timeout
 */
const generateSession = (): void => {
  sessionId = new Date().valueOf();
  resetTimeout();
};

/**
 * Reset the session timeout
 */
const resetTimeout = (): void => {
  if (sessionTimeout) {
    clearTimeout(sessionTimeout);
  }
  sessionTimeout = window.setTimeout(() => {
    clearSession();
  }, DEFAULT_TIMEOUT_DURATION);
};

/**
 * Clear the current session
 */
const clearSession = (): void => {
  sessionId = null;
  sessionTimeout = null;
  marketingSessionData.delete('riskSessionId');
};

/**
 * Get the current session ID, generating one if needed
 */
const getCurrentSessionId = (): number | null => {
  if (!sessionId) {
    generateSession();
  }
  return sessionId;
};

/**
 * Get the underlying AnalyticsBrowser instance
 * Use sparingly - prefer using the exported utility functions
 */
export function getAnalyticsBrowserInstance(): AnalyticsBrowser {
  return analytics;
}

/**
 * Set anonymous ID on initialization
 * Priority: cookie from marketing website > URL param > localStorage
 */
const initializeAnonymousId = async (): Promise<void> => {
  const searchParams = new URLSearchParams(window.location.search);
  const cookies = extractCookies();
  const cookieAnonymousId = cookies.get('ajs_anonymous_id');
  const urlAnonymousId = searchParams.get('anonymousId');
  const localStorageAnonymousId = localStorage.getItem('ajs_anonymous_id');

  // Priority: cookie > url param > localStorage
  const anonymousIdToUse =
    cookieAnonymousId || urlAnonymousId || localStorageAnonymousId;

  if (!anonymousIdToUse || anonymousIdToUse === '{}') {
    return;
  }

  try {
    const user = await analytics.user();
    user.anonymousId(anonymousIdToUse);
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error segment web setAnonymousId ${error}`,
    });
  }
};

// Initialize on module load
generateSession();
void initializeAnonymousId();
const analyticsConfig: AnalyticsConfig = initializeAnalyticsConfig();

/**
 * Get marketing session properties to include in all events
 * @returns Record of marketing session properties
 */
const getMarketingSessionProperties = (): Record<string, string> => {
  const properties: Record<string, string> = {};

  const amplitudeReplaySessionId = marketingSessionData.get(
    'amplitudeReplaySessionId',
  );
  if (amplitudeReplaySessionId) {
    properties['[Amplitude] Session Replay ID'] = amplitudeReplaySessionId;
  }

  const riskSessionId = marketingSessionData.get('riskSessionId');
  if (riskSessionId) {
    properties.risk_session_id = riskSessionId;
  }

  const shareID = marketingSessionData.get('shareid');
  if (shareID) {
    properties.shareid = shareID;
  }

  const roktClickId = marketingSessionData.get('roktClickId');
  if (roktClickId) {
    properties.passbackconversiontrackingID = roktClickId;
  }

  return properties;
};

// Utility functions
export const logEvent = async (
  event: string,
  properties: Record<string, unknown>,
): Promise<void> => {
  if (!event) {
    logBreadcrumb({
      level: 'info',
      message: `logSegmentEvent error:', 'parameter "event" is required'`,
    });
  }
  try {
    const referrer = await getItem(STORAGE_KEYS.PREVIOUS_PATH);
    await analytics.track(
      event,
      {
        ...properties,
        ...getMarketingSessionProperties(),
        ...getAppUpdateProperties(),
        ...(referrer && { referrer }),
        '[Amplitude] Version': displayVersion,
      },
      {
        integrations: {
          'Actions Amplitude': {
            session_id: getCurrentSessionId(),
          },
        },
      },
    );
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error segment web logEvent ${error}`,
    });
    logErrorWithContext(error, {
      featureArea: FeatureArea.CUSTOMER_ATTRIBUTION,
      responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
      severity: ErrorSeverity.HIGH,
      additionalContext: {
        action: 'log_event',
        event,
        hasProperties: Object.keys(properties).length > 0,
      },
    });
  }
};

export const logScreenEvent = async (
  screenName: string,
  properties: Record<string, unknown>,
): Promise<void> => {
  try {
    const referrer = await getItem(STORAGE_KEYS.PREVIOUS_PATH);
    await analytics.page(
      screenName,
      {
        ...properties,
        ...getMarketingSessionProperties(),
        ...getAppUpdateProperties(),
        ...(referrer && { referrer }),
        '[Amplitude] Version': displayVersion,
      },
      {
        integrations: {
          'Actions Amplitude': {
            session_id: getCurrentSessionId(),
          },
        },
      },
    );
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error segment web logScreenEvent ${error}`,
    });
    logErrorWithContext(error, {
      featureArea: FeatureArea.CUSTOMER_ATTRIBUTION,
      responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
      severity: ErrorSeverity.HIGH,
      additionalContext: {
        action: 'log_screen_event',
        event: screenName,
        hasProperties: Object.keys(properties).length > 0,
      },
    });
  }
};

export const identifyUser = async (
  userId: string,
  userTraits: Record<string, unknown>,
): Promise<void> => {
  try {
    await analytics.identify(userId, userTraits, {
      integrations: {
        'Actions Amplitude': {
          session_id: getCurrentSessionId(),
        },
      },
    });
    logBreadcrumb({
      level: 'info',
      message: `Setting identityId - Segment web ${userId}`,
    });
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error segment web identifyUser ${error}`,
    });
    logErrorWithContext(error, {
      featureArea: FeatureArea.CUSTOMER_ATTRIBUTION,
      responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
      severity: ErrorSeverity.HIGH,
      additionalContext: {
        action: 'identify_user',
        userId,
        hasUserTraits: Object.keys(userTraits).length > 0,
      },
    });
  }
};

/**
 * No-op on web: analytics-next dispatches events immediately, so there is no queue to flush.
 * Exists to keep parity with the native `SegmentInterface`.
 */
export const flush = async (): Promise<void> => {};

export const reset = async (): Promise<void> => {
  clearMarketingSessionData('riskSessionId');
};

export const getUserId = async (): Promise<string | undefined> => {
  const user = await analytics.user();
  return user.id() ?? undefined;
};

export const getAnonymousId = async (): Promise<string | undefined> => {
  const user = await analytics.user();
  const anonymousId = user.anonymousId();
  return anonymousId || undefined;
};

export const getDeviceId = async (): Promise<string | undefined> => {
  const user = await analytics.user();
  return user.anonymousId() as string;
};

export const userIdSession = async (): Promise<string> => {
  const userId = await getUserId();
  const anonymousId = await getAnonymousId();

  if (!userId) {
    logBreadcrumb({
      level: 'info',
      message: `userId is undefined, using anonymousId for event tracking.`,
    });
  }
  return userId || anonymousId || '';
};

export const updateRiskSessionId = (sessionId: string): void => {
  marketingSessionData.set('riskSessionId', sessionId);
};

export const setMarketingSessionData = (
  key: SessionDataKey,
  value: string,
): void => {
  marketingSessionData.set(key, value);
};

export const getMarketingSessionData = (
  key: SessionDataKey,
): string | undefined => {
  return marketingSessionData.get(key);
};

export const clearMarketingSessionData = (key: SessionDataKey): void => {
  marketingSessionData.delete(key);
};

export const addPlugin = (plugin: unknown): void => {
  console.log('Adding plugin to Segment Web client:', plugin);
  void analytics.register(plugin as Plugin);
};

export const setAmplitudeReplaySessionId = (sessionId: string): void => {
  marketingSessionData.set('amplitudeReplaySessionId', sessionId);
};

// Segment client wrapper that implements SegmentInterface
export const segmentClient: SegmentInterface = {
  getAnonymousId,
  getUserId,
  getDeviceId,
  userIdSession,
  logEvent,
  logScreenEvent,
  identifyUser,
  reset,
  flush,
  setMarketingSessionData,
  getMarketingSessionData,
  addPlugin,
};

//here purely for parity with current client hook based implementation.  will be removed in future refactor
export const logScreenEventFromConfig = async (
  key: string,
  customAttributes: Record<string, unknown> | null = null,
): Promise<void> => {
  const config: EventConfig | undefined =
    analyticsConfig.screenConfigs.get(key);
  if (config !== undefined) {
    await logScreenEvent(config.name, {
      name: config.name,
      source_name: config.source_name,
      category: config.category,
      subcategory: config.subcategory,
      ...customAttributes,
    });
  }
};

export const logPressableEventFromConfig = async (
  key: string,
  customAttributes: Record<string, unknown> | null = null,
): Promise<void> => {
  const config: EventConfig | undefined =
    analyticsConfig.pressableConfigs.get(key);
  if (config !== undefined) {
    await logEvent(config.name, {
      source_name: config.source_name,
      category: config.category,
      subcategory: config.subcategory,
      ...customAttributes,
    });
  }
};
